import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subscription, catchError, finalize, of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CourseService } from '@core/services/course/course.service';
import { CourseChapter } from '@core/models/course-chapter.model';
import { UserParticipation } from '@core/models/user-participation.response.model';
import { ParticipationService } from '@core/services/participation/participation.service';
import { Chapter } from '@core/models/chapter.model';
import { ChapterItem } from '@core/models/chapter-item.model';
import {
  CanonicalCourseChapterItemType,
  normalizeCourseChapterItemType,
} from '@core/utils/course-chapter-item-type.util';

@Component({
  selector: 'app-course-sidebar',
  templateUrl: './course-sidebar.component.html',
  styleUrl: './course-sidebar.component.css',
})
export class CourseSidebarComponent implements OnInit, OnDestroy {
  courseChapters$ = new BehaviorSubject<CourseChapter | null>(null);
  userParticipation$ = new BehaviorSubject<UserParticipation>({} as UserParticipation);
  loading = false;
  isCollapsed = false;
  errorMessage = '';
  courseUuid: string | null = null;
  currentItemUuid: string | null = null;
  private subscriptions = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly courseService: CourseService,
    private readonly participationService: ParticipationService
  ) {}

  ngOnInit(): void {
    if (window.innerWidth <= 768) {
      this.isCollapsed = true;
    }
    this.initializeCourseUuid();
    this.trackParamChanges();
  }

  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth > 768 && this.isCollapsed) {
      this.isCollapsed = false;
    }
  }

  private initializeCourseUuid(): void {
    let activeRoute = this.route.root;

    while (activeRoute?.firstChild) {
      activeRoute = activeRoute.firstChild;
    }

    const routeSub = activeRoute.paramMap.subscribe((paramMap) => {
      this.courseUuid = paramMap.get('uuid');
      this.currentItemUuid = paramMap.get('questionSet') || paramMap.get('lesson') || null;
      if (this.courseUuid) {
        this.fetchCourseChapters(this.courseUuid);
        this.getUserParticipation(this.courseUuid);
      }
    });

    this.subscriptions.add(routeSub);
  }

  private fetchCourseChapters(courseUuid: string): void {
    this.loading = true;
    const chapterSub = this.courseService
      .getCourseChapters(courseUuid)
      .pipe(
        catchError((error) => {
          this.errorMessage = error.message;
          return of(null);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe((data) => {
        if (data) {
          this.courseChapters$.next(data);
        }
      });

    this.subscriptions.add(chapterSub);
  }

  private trackParamChanges(): void {
    const routeChangeSub = this.router.events
      .pipe(filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        let activeRoute = this.route.root;
        while (activeRoute?.firstChild) {
          activeRoute = activeRoute.firstChild;
        }

        const paramMap = activeRoute.snapshot.paramMap;
        const newUuid = paramMap.get('uuid');
        this.currentItemUuid = paramMap.get('questionSet') || paramMap.get('lesson') || null;
        this.getUserParticipation(newUuid!);
        this.fetchCourseChapters(newUuid!);
      });

    this.subscriptions.add(routeChangeSub);
  }

  private getUserParticipation(courseUuid: string): void {
    this.loading = true;

    const participationRouteSub = this.participationService
      .getUserParticipationDetails(courseUuid)
      .pipe(
        catchError((error) => {
          this.errorMessage = error.message;
          return of(null);
        }),
        finalize(() => (this.loading = false))
      )
      .subscribe((response) => {
        if (response?.data) {
          this.userParticipation$.next(response.data);
        }
      });

    this.subscriptions.add(participationRouteSub);
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  getCourseChapterItemRoute(
    rawType: string | null | undefined,
    itemUuid: string
  ): Array<string | null> | null {
    const itemType = normalizeCourseChapterItemType(rawType);

    if (itemType === CanonicalCourseChapterItemType.Lesson) {
      return ['/chapter', this.courseUuid, 'lesson', itemUuid];
    }

    if (itemType === CanonicalCourseChapterItemType.QuestionSet) {
      return ['/chapter', this.courseUuid, 'test', itemUuid];
    }

    return null;
  }

  isChapterCompleted(chapter: Chapter): boolean {
    return chapter.chapter_items.every((item: ChapterItem) => item.content.user_progress === 100);
  }

  isChapterCurrent(chapter: Chapter): boolean {
    return chapter.chapter_items.some(
      (item: ChapterItem) => item.content.uuid === this.currentItemUuid
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
